import {
  Button,
  ButtonProps as LibButtonProps,
} from '@im/ui-lib/zenith/Button';
import {
  CartOptionsTo,
  SelectedOptionsTO,
  SubsDataResponse,
} from '../CloudAddToCartButton/common/types';
import { DataAttributes, ToastIntent } from '@im/ui-lib/zenith/common';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAddToQuote } from './hooks';
import { useLocalization, useTranslate } from '@im/react-localization';
import { ButtonType } from './types/Button.type';
import { Icon } from '@im/ui-lib/zenith/Icon';
import Box from '@mui/material/Box';
import { LoadingIndicator } from '@im/ui-lib/zenith/LoadingIndicator';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { AddToQuoteToast } from '../AddToQuoteToast';
import ValidationPopup from '../CloudAddToCartButton/internal/ValidationPopup';
import {
  checkResellerOnly,
  getExistingDependents,
} from '../CloudAddToCartButton/common/commonFunctions';
import {
  useGetQuoteSummaryQuery,
  useProductSummaryMutate,
} from './hooks/use-get-quote-summary';
import { subscriptionAddtoQuoteBody } from './internal';
import Typography from '@mui/material/Typography';

type CloudAddToQuoteButtonProps = {
  allSubscriptionOptions?: SubsDataResponse;
  buttonProps?: LibButtonProps;
  buttonStyles?: SxProps<Theme>;
  buttonText?: string;
  buttonType: ButtonType;
  cartRequest: CartOptionsTo[];
  dataAttributes?: DataAttributes;
  disabled?: boolean;
  iconButton?: boolean;
  isMandatoryPlan?: boolean;
  isPending?: boolean;
  isSuccess?: boolean;
  onClick?: () => void;
  planQty?: number;
  planTitle?: string;
  selectedOptions: SelectedOptionsTO[];
  title?: string;
};
const CloudAddToQuoteButton = (props: CloudAddToQuoteButtonProps) => {
  const {
    allSubscriptionOptions,
    buttonProps: addlButtonProps,
    buttonStyles,
    buttonText,
    buttonType,
    cartRequest,
    dataAttributes,
    isMandatoryPlan,
    isPending,
    onClick,
    planQty,
    planTitle,
    selectedOptions,
  } = props;

  const { addToQuote, quoteEditorURL, quoteInfo } = useAddToQuote();
  const quoteSummary = useGetQuoteSummaryQuery();
  const translate = useTranslate();
  const theme = useTheme();
  const productSummary = useProductSummaryMutate();
  const { formatCurrency } = useLocalization();

  const [buttonProps, setButtonProps] = useState({
    isAdding: false,
    isSuccessful: false,
  });
  const [addToQuoteMessage, setAddToQuoteMessage] =
    useState<ToastIntent | null>(null);
  const [validationOptions, setValidationOptions] = useState({
    option1: '',
    option2: '',
    result: '',
  });
  const [validationError, setValidationError] = useState('');
  const [cartValidationPopup, setCartValidationPopup] = useState(false);

  const exclusiveItemForReseller = translate(
    'ProductDetailsResources.ExclusiveItemForReseller'
  );
  const disableCart =
    selectedOptions?.length === 0 ||
    buttonProps.isAdding ||
    addlButtonProps?.disabled ||
    isPending;

  useEffect(() => {
    if (buttonProps.isSuccessful) {
      const timer = setTimeout(() => {
        setButtonProps({ isAdding: false, isSuccessful: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [buttonProps.isSuccessful]);

  const getPlanInfo = async () => {
    let queryurl = '';
    const productId = selectedOptions?.[0]?.option?.resourceApsId;
    const subscriptionPeriod = selectedOptions?.[0]?.subscriptionPeriod;
    if (subscriptionPeriod) {
      queryurl = `,'SelectedSubscriptionPeriod':'${encodeURIComponent(
        subscriptionPeriod
      )}'${queryurl}`;
    }
    const response = await productSummary.mutateAsync({
      queryurl,
      skulist: `{'partNumber':'${productId}','quantity':'1'}`,
    });

    return response?.productSummaries;
  };

  const handleAddToQuote = async () => {
    try {
      if (onClick) {
        onClick();
        return;
      }
      const existingQuoteData = quoteSummary;

      if (cartRequest && cartRequest.length > 0) {
        setButtonProps({ isAdding: true, isSuccessful: false });
        const dependentExists = getExistingDependents(
          selectedOptions,
          existingQuoteData,
          allSubscriptionOptions
        );

        if (dependentExists && dependentExists?.result !== '') {
          setValidationOptions(dependentExists);
          setValidationError(dependentExists?.result);
          setCartValidationPopup(true);
          return false;
        } else if (
          checkResellerOnly(
            { data: { isEndUserSelected: !!quoteInfo.endUserInformation } },
            selectedOptions,
            existingQuoteData
          )
        ) {
          dependentExists.result = exclusiveItemForReseller;
          setValidationOptions(dependentExists);
          setValidationError(dependentExists.result);
          setCartValidationPopup(true);
          return false;
        } else if (selectedOptions) {
          let allOptions = allSubscriptionOptions?.data?.subOptions;
          if (!allSubscriptionOptions) {
            allOptions = await getPlanInfo();
          }
          const obj = subscriptionAddtoQuoteBody(allOptions, selectedOptions);
          await addToQuote('subscription', null, obj);
          setAddToQuoteMessage('success');
        }
        setButtonProps({ isAdding: false, isSuccessful: true });
      }
    } catch (e) {
      setButtonProps({ isAdding: false, isSuccessful: false });
      setAddToQuoteMessage('error');
    }
  };

  const onHideToast = () => setAddToQuoteMessage(null);

  const description = useMemo(() => {
    const qty = isMandatoryPlan ? `${planQty} x ` : '';
    const title = planTitle ?? selectedOptions?.[0]?.option?.title;
    const addOnsQuantity = isMandatoryPlan
      ? selectedOptions?.length - 1
      : selectedOptions?.length;

    const estTotal = selectedOptions?.reduce((sum, option) => {
      const customerPrice = option?.optionsPricing?.customerPrice || 0;
      return sum + customerPrice;
    }, 0);

    return (
      <>
        <Typography
          sx={{ color: '#fff' }}
          variant="text-sm-regular"
        >{`${qty} ${title}`}</Typography>
        {addOnsQuantity > 0 && `+${addOnsQuantity} Add-ons`}
        <Typography sx={{ color: '#fff' }} variant="text-sm-regular">
          {formatCurrency(estTotal || 0)}
        </Typography>
      </>
    );
  }, [formatCurrency, isMandatoryPlan, planQty, planTitle, selectedOptions]);

  const getButtonText = useCallback(() => {
    if (buttonProps.isSuccessful) {
      if (buttonType === ButtonType.iconwithcheckmark) {
        return <Icon iconId="Check" />;
      } else {
        return translate('Common.Added');
      }
    } else if (buttonProps.isAdding || isPending) {
      const showText = [ButtonType.textwithicon, ButtonType.text].includes(
        buttonType
      );
      return (
        <Box alignItems="center" display="flex" gap={'8px'}>
          <LoadingIndicator
            color="#fff"
            loadingStyle="line-simple"
            spinnerSize={20}
            value={40}
            variant="indeterminate"
          />
          {showText ? translate('Common.Adding') : null}
        </Box>
      );
    } else if (buttonType === ButtonType.icon) {
      return <Icon iconId="QuotePlus" />;
    } else {
      const text = buttonText
        ? buttonText.charAt(0).toUpperCase() + buttonText.slice(1).toLowerCase()
        : translate('ProductConfig.AddToQuote');
      return text;
    }
  }, [
    buttonProps.isAdding,
    buttonProps.isSuccessful,
    buttonText,
    buttonType,
    isPending,
    translate,
  ]);

  return (
    <>
      <Button
        {...dataAttributes}
        {...addlButtonProps}
        disabled={disableCart}
        fullWidth
        onClick={handleAddToQuote}
        size="medium"
        startIcon={
          buttonType === ButtonType.textwithicon ? (
            <Icon iconId="QuotePlus" />
          ) : null
        }
        sx={{
          ...buttonStyles,
          ...(buttonProps.isSuccessful
            ? {
                backgroundColor: `${theme.palette.success[700]}!important`,
              }
            : undefined),
        }}
        variant="primary"
      >
        {getButtonText()}
      </Button>
      {validationError !== '' && (
        <ValidationPopup
          mutuallyExclPopup={cartValidationPopup}
          setMutuallyExclPopup={setCartValidationPopup}
          validationResult={validationOptions?.result}
        />
      )}
      {addToQuoteMessage && (
        <AddToQuoteToast
          description={description}
          onHideToast={onHideToast}
          quoteEditorURL={quoteEditorURL}
          show
          toastIntent={addToQuoteMessage}
        />
      )}
    </>
  );
};

export default CloudAddToQuoteButton;
